import type { StorageType  } from './storage-type.enum';
import type { StorageKeyBuilderService } from './storage-key-builder.service';
import type { StorageKeyBuilderStrategy } from './storage-key-builder-strategy.enum';

/**
 * BaseStorageService
 * Abstract class to provide basic access and utility methods to browser's storage
 */
export abstract class BaseStorageService {

  protected storage: Storage;

  protected constructor(private window: Window,
                        private storageKeyBuilderService: StorageKeyBuilderService,
                        private storageKeyBuilderStrategy: StorageKeyBuilderStrategy,
                        private storageType: StorageType) {
    this.storage = this.window[this.storageType]; // convenience shortcut
  }

  /**
   * getItem
   * Retrieves value for given key
   *
   * @param key
   * @returns Value to be stored
   */
  public getItem<T>(key: string): T {
    const item = this.storage.getItem(this.getQualifiedKey(key));

    if (item === null || item === 'null') {
      return null;
    }
    if (item.charAt(0) === '{' || item.charAt(0) === '[' || this.isStringNumber(item)) {
      return JSON.parse(item);
    }
    return item as T;
  }

  /**
   * setItem
   * Set value for a key on the storage
   *
   * @param key
   * @param value
   */
  public setItem(key: string, value: unknown): void {
    if (value === undefined) {
      value = null;
    } else if ((value !== null && typeof value === 'object') ||
      Array.isArray(value) ||
      (typeof Number(value) === 'number') && typeof value === 'number') {
      value = JSON.stringify(value);
    }
    this.storage.setItem(this.getQualifiedKey(key), value as string);
  }

  /**
   * removeItem
   * Remove an entry from storage
   *
   * @param key
   */
  public removeItem(key: string): void {
    this.storage.removeItem(this.getQualifiedKey(key));
  }

  /**
   * forEach
   * Invokes callback for all keys on the storage object
   *
   * @param callback
   */
  public forEach(callback: (value: unknown, key: string) => void): void {
    for (const entry of Object.keys(this.storage)) {
      callback(entry, this.storage[entry]);
    }
  }

  /**
   * getQualifiedKey
   * Retrieves qualified key (ie user sends 'MyKey' and returns 'tdWbMyKey')
   * from key builder service
   *
   * @param key
   * @returns
   */
  protected getQualifiedKey(key: string): string {
    return this.storageKeyBuilderService.getQualifiedKey(key, this.storageKeyBuilderStrategy);
  }

  private isStringNumber(num: string): boolean {
    const s = num.replace(/["]'/g, '').trim();
    if (s.length === 0) return false; // prevent JSON.parse('') path
    return !isNaN(Number(s));
  }
}
